<template>
  <d-card
    v-if="loaded"
    :ref="'order-creator-product-' + productIndex"
    class="mt-4">
    <d-card-body class="px-2 px-lg-3 py-3 py-lg-4">
      <div
        :ref="'product-container-' + productIndex"
        class="d-flex align-items-start product-info flex-wrap flex-md-nowrap">
        <div class="w-50 product-info-select d-flex flex-column flex-md-row">
          <div class="mr-2 pt-0 pb-2 pt-md-2 my-auto">
            {{ productIndex + 1 }}.p
          </div>
          <div class="w-100">
            <small class="font-weight-bold">
              Wybierz produkt:
            </small>
            <template v-if="isProductAvailable">
              <div
                :class="{
                  'form-control': true,
                  'is-select': true,
                  'is-invalid': formErrors && formErrors['product-id-' + productIndex] && formErrors['product-id-' + productIndex].length,
                  'vs-wrap-text': true
                }">
                <v-select
                  v-model="currentProduct.product_id"
                  :ref="'product-' + productIndex"
                  :id="'product-' + productIndex"
                  :disabled="isDisabled"
                  :filterBy="filterByForProducts"
                  :options="customerProducts"
                  :reduce="(product) => product.id"
                  :searchable="true"
                  :selectable="(option) => option.isAvailable"
                  @input="changeProduct()"
                  @search="debouncedFetchProducts"
                  class="dropdown-wide"
                  label="name"
                  placeholder="Wybierz produkt">
                  <template slot="no-options">
                    <template v-if="!productsEmptyResults">Trwa wyszukiwanie&hellip;</template>
                    <template v-else-if="productsEmptyResults">Brak produktów pasujących do podanej frazy&hellip;</template>
                  </template>
                  <template slot="option" slot-scope="option">
                    {{ option.name }}
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    {{ option.name }}
                  </template>
                </v-select>
              </div>

              <validation-provider
                rules="required"
                :name="'product-id-' + productIndex"
                v-slot="{ errors }">
                <input
                  type="hidden"
                  :value="currentProduct.product_id"
                  :state="errors.length ? 'invalid' : null" />
              </validation-provider>
            </template>
            <template v-else>
              <d-input
                :disabled="true"
                :value="currentProduct.name"/>
            </template>

            <template v-if="isProductAvailable && isProductVariantAvailable">
              <div
                v-if="hasVariants"
                :class="{
                  'form-control': true,
                  'is-select': true,
                  'is-invalid': formErrors && formErrors['product-variant-id-' + productIndex] && formErrors['product-variant-id-' + productIndex].length,
                  'mt-3': true,
                  'vs-wrap-text': true
                }">
                <v-select
                  :ref="'product-' + productIndex + '-variant'"
                  v-model="currentProduct.variant_product_id"
                  :disabled="isDisabled"
                  :filterBy="filterByForVariants"
                  :options="getProductVariants(currentProduct.product_id)"
                  :reduce="variant => variant.id"
                  :searchable="true"
                  :selectable="(option) => option.isAvailable"
                  @input="changeProductVariant()"
                  class="dropdown-wide"
                  label="variant_name"
                  placeholder="Wybierz wariant">
                  <template slot="no-options">
                    <template v-if="!variantsEmptyResults">Trwa wyszukiwanie&hellip;</template>
                    <template v-if="variantsEmptyResults">Brak wariantów pasujących do podanej frazy&hellip;</template>
                  </template>
                  <template
                    slot="option"
                    slot-scope="option">
                    <div class="d-center">
                      {{ option.name }}<br><small>Kod EAN: {{ option.code }}</small>
                    </div>
                  </template>
                  <template
                    slot="selected-option"
                    slot-scope="option">
                    <div class="selected d-center">
                      {{ option.name }}<br><small>Kod EAN: {{ option.code }}</small>
                    </div>
                  </template>
                </v-select>

                <validation-provider
                  rules="required"
                  :name="'product-variant-id-' + productIndex"
                  v-slot="{ errors }">
                  <input
                    type="hidden"
                    :value="currentProduct.variant_product_id"
                    :state="errors.length ? 'invalid' : null" />
                </validation-provider>
              </div>
            </template>
            <template v-else>
              <d-input
                class="mt-2"
                :disabled="true"
                :value="currentProduct.variant_name"/>
            </template>
            <small
              v-if="isDuplicated"
              class="d-block mt-2 text-warning font-weight-bold">
              Dodano więcej niż 1 taki produkt.
            </small>
          </div>
        </div>

        <div class="w-50 d-flex flex-wrap product-info-pricing">
          <d-row class="flex-lg-nowrap ml-2 mr-0">
            <div class="ml-2">
              <small class="font-weight-bold">
                <template v-if="!currentProduct.is_package">
                  Cena brutto za sztukę:
                </template>
                <template v-else>
                  Cena brutto za pakiet:
                </template>
              </small>
              <validation-provider
                rules="required|min_value:0"
                :name="'product-price-' + productIndex"
                v-slot="{ errors }">
                <d-input-group
                  append="PLN">
                  <d-input
                    :ref="'product-' + productIndex + '-price'"
                    :min="1"
                    :disabled="isDisabled || isWorker || !isProductAvailable || !isProductVariantAvailable"
                    v-model="currentProduct.price_before_discount"
                    @input="recalculatePrice()"
                    :state="errors.length ? 'invalid' : null"
                    type="number" />
                </d-input-group>
              </validation-provider>

              <small
                v-if="currentProduct.product_id && !currentProduct.variant_product_id && productDefaultPricesAndTaxes[currentProduct.product_id]"
                class="text-primary"
                :key="currentProduct.product_id">
                <template v-if="currentProduct.is_package">
                  Cena pakietu {{ roundProperly(productDefaultPricesAndTaxes[currentProduct.product_id].package_price) }} zł.
                </template>
                <template v-else>
                  Cena z cennika: {{ roundProperly(productDefaultPricesAndTaxes[currentProduct.product_id].price) }} zł
                </template>
              </small>

              <small
                v-if="currentProduct.variant_product_id && productDefaultPricesAndTaxes[currentProduct.variant_product_id]"
                class="text-primary"
                :key="currentProduct.variant_product_id">
                <template v-if="currentProduct.is_package">
                  Cena z cennika: {{ roundProperly(productDefaultPricesAndTaxes[currentProduct.variant_product_id].package_price) }} zł
                </template>
                <template v-else>
                  Cena z cennika: {{ roundProperly(productDefaultPricesAndTaxes[currentProduct.variant_product_id].price) }} zł
                </template>
              </small>

              <small
                v-if="!isProductAvailable"
                class="text-danger">
                Tego produktu nie ma już na liście dostępnych produktów.
              </small>

              <small
                v-if="!isProductVariantAvailable"
                class="text-danger">
                Tego wariantu produktu nie ma już na liście dostępnych produktów.
              </small>
            </div>

            <div
              class="ml-2"
              style="width: 7.5rem;">
              <small class="font-weight-bold">
                <template v-if="!currentProduct.is_package">
                  Ilość:
                </template>
                <template v-else>
                  Ilość pakietów:
                </template>
              </small>

              <validation-provider
                :rules="'required|min_value:1|max_value:' + currentProductMaxQuantity"
                :name="'product-quantity-' + productIndex"
                v-slot="{ errors }">
                <d-input-group
                  append="szt.">
                  <d-input
                    :ref="'product-' + productIndex + '-quantity'"
                    :min="1"
                    :max="currentProductMaxQuantity"
                    :disabled="isDisabled || !isProductAvailable || !isProductVariantAvailable"
                    v-model="currentProduct.quantity"
                    :state="errors.length ? 'invalid' : null"
                    type="number" />
                </d-input-group>
              </validation-provider>
              <small
                v-if="currentProduct.quantity > currentProductMaxQuantity"
                class="text-danger">
                Aktualna dostępność to {{ currentProductMaxQuantity }} szt.
              </small>
            </div>

            <div
              class="ml-3"
              style="width: 120px;">
              <small class="font-weight-bold">
                Koszt całkowity:
              </small>

              <div :class="{ 'pt-1': productTotals.price === productTotals.price_before_discount }">
                <span class="text-primary">
                  {{ roundProperly(productTotals.price) }} zł
                </span>

                <template v-if="productTotals.price !== productTotals.price_before_discount">
                  <br>
                  <small class="text-danger">
                    <del>
                      {{ roundProperly(productTotals.price_before_discount) }} zł
                    </del>
                  </small>
                </template>
              </div>
            </div>
          </d-row>

          <d-row class="ml-2 mr-0 mt-2">
            <div
              v-if="currentProduct.product_id && !currentProduct.variant_product_id && productDefaultPricesAndTaxes[currentProduct.product_id] && productDefaultPricesAndTaxes[currentProduct.product_id].available_in_package"
              class="ml-2 d-flex">
              <d-checkbox
                toggle
                toggle-small
                class="mr-0 my-0 pr-4"
                inline
                :disabled="isDisabled"
                @change="changeProduct()"
                v-model="currentProduct.is_package">
                Zamów w pakiecie
              </d-checkbox>
              <small
                v-if="currentProduct.is_package"
                class="pt-1">
                1 pakiet zawiera <strong>{{ productDefaultPricesAndTaxes[currentProduct.product_id].quantity_in_package }}szt.</strong> produktu.
              </small>
            </div>
            <div
              v-if="currentProduct.variant_product_id && productDefaultPricesAndTaxes[currentProduct.variant_product_id] && productDefaultPricesAndTaxes[currentProduct.variant_product_id].available_in_package"
              class="ml-2 d-flex">
              <d-checkbox
                toggle
                toggle-small
                class="mr-0 my-0 pr-4"
                inline
                :disabled="isDisabled"
                @change="changeProductVariant()"
                v-model="currentProduct.is_package">
                Zamów w pakiecie
              </d-checkbox>
              <small
                v-if="currentProduct.is_package"
                class="pt-1">
                1 pakiet zawiera <strong>{{ productDefaultPricesAndTaxes[currentProduct.variant_product_id].quantity_in_package }}szt.</strong> produktu.
              </small>
            </div>
          </d-row>
        </div>

        <d-button
          :disabled="isDisabled"
          theme="danger"
          outline
          class="ml-2 mt-4"
          @click.prevent="removeProduct">
          Usuń
        </d-button>
      </div>
    </d-card-body>
  </d-card>
</template>

<script>
import debounce from 'lodash.debounce';
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import CustomDecimals from '@/utils/CustomDecimals.js';

export default {
  name: 'order-creator-product',
  props: [
    'allProducts',
    'customerId',
    'initialCustomerProducts',
    'isDisabled',
    'isDuplicated',
    'isEdit',
    'isWorker',
    'orderForm',
    'product',
    'productIndex'
  ],
  computed: {
    formErrors () {
      return this.orderForm.errors;
    },
    hasVariants () {
      return this.customerProducts.find(product => product.id === this.currentProduct.product_id && product.children.length);
    },
    isProductAvailable () {
      return !(this.currentProduct.product_id && !this.currentProduct.variant_product_id && !this.productDefaultPricesAndTaxes[this.currentProduct.product_id]);
    },
    isProductVariantAvailable () {
      return !(this.currentProduct.variant_product_id && !this.productDefaultPricesAndTaxes[this.currentProduct.variant_product_id]);
    },
    productDefaultPricesAndTaxes () {
      let productsIndex = {};

      for (let i = 0; i < this.customerProducts.length; i++) {
        let product = this.customerProducts[i];

        productsIndex[product.id] = {
          price: product.price,
          vatRate: product.vat_rate,
          discount: parseFloat(product.discount)
        };

        if (product.type !== 'VARIANT') {
          productsIndex[product.id].available_in_package = product.available_in_package;
          productsIndex[product.id].package_price = product.package_price;
          productsIndex[product.id].quantity_in_package = product.quantity_in_package;
          productsIndex[product.id].stock = product.stock;
          productsIndex[product.id].minimal_stock = product.minimal_stock;
        }

        if (product.children.length) {
          for (let j = 0; j < product.children.length; j++) {
            let child = product.children[j];

            productsIndex[child.id] = {
              price: child.price,
              vatRate: child.vat_rate,
              discount: parseFloat(child.discount),
              available_in_package: child.available_in_package,
              package_price: child.package_price,
              quantity_in_package: child.quantity_in_package,
              stock: child.stock,
              minimal_stock: child.minimal_stock
            };
          }
        }
      }

      return productsIndex;
    },
    productTotals () {
      return {
        price: this.roundProperly(this.currentProduct.price * this.currentProduct.quantity),
        price_before_discount: this.roundProperly(this.currentProduct.price_before_discount * this.currentProduct.quantity)
      };
    },
    currentProductMaxQuantity () {
      if (!this.currentProduct || !(this.currentProduct.product_id || this.currentProduct.variant_product_id)) {
        return 0;
      }

      if (!this.productDefaultPricesAndTaxes[this.currentProduct.variant_product_id] && !this.productDefaultPricesAndTaxes[this.currentProduct.product_id]) {
        return 0;
      }

      let currentStock = 0;
      let currentMinimalStock = 0;
      if (this.currentProduct.variant_product_id) {
        currentStock = this.productDefaultPricesAndTaxes[this.currentProduct.variant_product_id].stock;
        currentMinimalStock = this.productDefaultPricesAndTaxes[this.currentProduct.variant_product_id].minimal_stock;
      } else {
        currentStock = this.productDefaultPricesAndTaxes[this.currentProduct.product_id].stock;
        currentMinimalStock = this.productDefaultPricesAndTaxes[this.currentProduct.product_id].minimal_stock;
      }
      if (!currentStock) {
        return 0;
      }

      if (this.currentProduct.is_package) {
        return Math.floor((currentStock - currentMinimalStock) / this.productDefaultPricesAndTaxes[this.currentProduct.variant_product_id].quantity_in_package);
      }

      return Math.max(currentStock - currentMinimalStock, 0);
    }
  },
  data () {
    return {
      currentProduct: null,
      customerProducts: [],
      loaded: false,
      loadedCustomerProducts: false,
      productsEmptyResults: false,
      variantsEmptyResults: false,
    };
  },
  mounted () {
    this.$bus.$on('order-products-reset', this.resetProduct);
    this.resetProduct();
    this.loaded = true;

    // only for newly added product
    Vue.nextTick(() => {
      if (!this.product.product_id) {
        this.scrollToProduct();
        this.setFocusToProductDropDown();
      }
    })
  },
  methods: {
    resetProduct () {
      this.currentProduct = this.product;
      this.setInitialCustomerProducts();
      this.setProductsAvailablitySummary(this.customerProducts);
    },
    setInitialCustomerProducts () {
      this.customerProducts = this.initialCustomerProducts;
      if (this.currentProduct && this.currentProduct.product_id && !this.customerProducts.find(item => item.id === this.currentProduct.product_id)) {
        this.customerProducts.push(this.$parent.getProduct(this.currentProduct.product_id));
      }
    },
    filterByForVariants (option, label, search) {
      this.variantsEmptyResults = false;
      if (((option.name).toLocaleLowerCase('pl').indexOf(search.toLocaleLowerCase('pl')) > -1) || ((option.code).toLocaleLowerCase('pl').indexOf(search.toLocaleLowerCase('pl')) > -1)) {
        return true;
      }

      this.variantsEmptyResults = true;
      return false;
    },
    filterByForProducts (option) {
      this.productsEmptyResults = false;

      if (option.name.length > 0) {
        return true;
      }

      this.productsEmptyResults = true;
      return false;
    },
    // eslint-disable-next-line
    debouncedFetchProducts: debounce(function (searchPhrase, loadingCallback) {
      this.fetchProducts(searchPhrase, loadingCallback);
    }, 500),
    fetchProducts (searchPhrase, loadingCallback) {
      if (!searchPhrase.length) {
        this.setInitialCustomerProducts();
        return;
      }
      this.productsEmptyResults = false;
      loadingCallback(true);

      let endpointUrl = '/api/products/items';
      let paramsData = {};

      if (searchPhrase !== '') {
        paramsData.customer_id = this.customerId
        paramsData.where = {
          search: searchPhrase
        };
        paramsData.per_page = 500
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: endpointUrl,
        method: 'get',
        outputKey: 'customerProducts',
        loadedKey: 'loadedCustomerProducts',
        errorKey: 'loadError',
        params: paramsData,
        successAction: (response) => {
          if (!response.length) {
            this.productsEmptyResults = true;
          }

          this.setProductsAvailablitySummary(this.customerProducts);
          loadingCallback(false);
        }
      });
    },
    scrollToProduct () {
      Vue.nextTick(() => {
        let refName = 'product-container-' + this.productIndex;
        if (this.$refs[refName]) {
          this.$refs[refName].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
        }
      })
    },
    setFocusToProductDropDown () {
      let refName = 'product-' + this.productIndex;
      if (this.$refs[refName] && this.$refs[refName].$el) {
        this.$refs[refName].$el.querySelector('input.vs__search').focus();
      }
    },
    setFocusToNextField (productID) {
      let productVariants = this.getProductVariants(productID);
      if (!productVariants.length) {
        this.setFocusToQuantityInput();
        return;
      }

      this.setFocusToVariantSelect(productVariants);
    },
    setFocusToQuantityInput () {
      let refName = 'product-' + this.productIndex + '-quantity';
      if (this.$refs[refName] && this.$refs[refName].$el) {
        // below double focus is fix for Safari
        this.$refs[refName].$el.focus();
        Vue.nextTick(() => {
          this.$refs[refName].$el.focus();
        })
      }
    },
    setFocusToVariantSelect (productVariants) {
      let refName = 'product-' + this.productIndex + '-variant';
      if (this.$refs[refName] && this.$refs[refName].$el && this.$refs[refName].$el.querySelector('input.vs__search')) {
        this.$refs[refName].$el.querySelector('input.vs__search').focus();
      }

      this.setDefaultVariant(productVariants);
    },
    setDefaultVariant (productVariants) {
      if (productVariants.length === 1) {
        Vue.set(this.currentProduct, 'variant_product_id', productVariants[0].id);
        this.changeProductVariant();
      }
    },
    changeProduct () {
      Vue.set(this.currentProduct, 'variant_product_id', null);
      let productID = this.currentProduct.product_id;

      Vue.nextTick(() => {
        if (productID) {
          let productDefaultsPrices = this.productDefaultPricesAndTaxes[productID];

          if (this.currentProduct.is_package) {
            Vue.set(this.currentProduct, 'price_before_discount', productDefaultsPrices.package_price);
          } else {
            Vue.set(this.currentProduct, 'price_before_discount', productDefaultsPrices.price);
          }

          Vue.set(this.currentProduct, 'vat_rate', productDefaultsPrices.vatRate);
          Vue.set(this.currentProduct, 'discount', productDefaultsPrices.discount || 0);

          Vue.set(this.currentProduct, 'stock', productDefaultsPrices.stock || 0);

          this.setFocusToNextField(productID);
        } else {
          Vue.set(this.currentProduct, 'price_before_discount', '');
          Vue.set(this.currentProduct, 'vat_rate', '');
          Vue.set(this.currentProduct, 'stock', 0);
        }
      })
    },
    changeProductVariant () {
      let productID = this.currentProduct.variant_product_id;
      let parentProductID = this.currentProduct.product_id;

      Vue.nextTick(() => {
        if (productID) {
          let productDefaultsPrices = this.productDefaultPricesAndTaxes[productID]

          if (this.currentProduct.is_package) {
            Vue.set(this.currentProduct, 'price_before_discount', productDefaultsPrices.package_price);
          } else {
            Vue.set(this.currentProduct, 'price_before_discount', productDefaultsPrices.price);
          }

          Vue.set(this.currentProduct, 'vat_rate', productDefaultsPrices.vatRate);
          Vue.set(this.currentProduct, 'discount', productDefaultsPrices.discount || 0);
          Vue.set(this.currentProduct, 'stock', productDefaultsPrices.stock);
        } else if (parentProductID) {
          let parentProductDefaultsPrices = this.productDefaultPricesAndTaxes[parentProductID];
          Vue.set(this.currentProduct, 'price_before_discount', parentProductDefaultsPrices.price);
          Vue.set(this.currentProduct, 'price_before_discount', parentProductDefaultsPrices.vatRate);
          Vue.set(this.currentProduct, 'discount', parentProductDefaultsPrices.discount || 0);
        } else {
          Vue.set(this.currentProduct, 'price_before_discount', '');
          Vue.set(this.currentProduct, 'vat_rate', '');
          Vue.set(this.currentProduct, 'discount', '');
          Vue.set(this.currentProduct, 'stock', 0);
        }

        this.setFocusToQuantityInput();
      })
    },
    getProductVariants (productID) {
      let foundedProduct = this.customerProducts.filter(product => product.id === productID);

      if (foundedProduct[0]) {
        this.setProductsAvailablitySummary(foundedProduct[0].children);
        return foundedProduct[0].children;
      }

      return [];
    },
    recalculatePrice () {
      let fullPrice = this.currentProduct.price_before_discount;
      let finalDiscount = 0;

      if (this.customerId) {
        if (!this.isEdit) {
          let productID = this.currentProduct.product_id;
          let productDefaults = this.productDefaultPricesAndTaxes[productID];

          if (productDefaults && productDefaults.discount !== null && !Number.isNaN(productDefaults.discount)) {
            finalDiscount = productDefaults.discount;
          }
        } else if (this.currentProduct.discount !== null && !Number.isNaN(this.currentProduct.discount)) {
          finalDiscount = this.currentProduct.discount;
        }
      }

      this.currentProduct.discount = finalDiscount;
      this.currentProduct.price = this.roundProperly(fullPrice - (fullPrice * (finalDiscount / 100)));
    },
    roundProperly (value, decimals = 2) {
      return CustomDecimals.roundProperly(value, decimals);
    },
    removeProduct () {
      this.$parent.removeProduct(this.productIndex);
    },
    setProductsAvailablitySummary (productsArr) {
      for (let i = 0; i < productsArr.length; i++) {
        productsArr[i].isAvailable = this.getIsProductAvailable(productsArr[i]);
      }
    },
    getIsProductAvailable (product) {
      if (product.availability < 1) {
        return false;
      }

      if (product.type === 'VARIANT') {
        return !!(product.children.find(item => item.availability && item.stock > item.minimal_stock));
      }

      return product.stock > product.minimal_stock;
    },
    beforeDestroy () {
      this.$bus.$off('order-products-reset', this.resetProduct);
    }
  }
}
</script>
